import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useStrategicImperativeFormConfig from './StrategicImperativeForm.config';
import { strategicImperativeToBE } from 'basics/transformers/StrategicImperative.transformer';
import {
  SiFormCanceltCallbackType,
  SiFormSubmitCallbackType,
} from 'components/StrategicImperativeForm/StrategicImperativeForm.types';
import { useCurrentUserState } from 'contexts/user';
import { StrategicImperative } from 'generated/maya.types';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useStrategicImperativeCreate = (
  strategicImperative: StrategicImperative | undefined,
  submitCallback: SiFormSubmitCallbackType,
  cancelCallback: SiFormCanceltCallbackType,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const { getValues, providerFields, resetForm, useWatchForm, setFieldShowError } = useYodaCreateForm();
  const { user } = useCurrentUserState();
  const { strategicImperativeFormConfig } = useStrategicImperativeFormConfig(strategicImperative, user);
  const { isValid } = useWatchForm();

  useEffect(() => {
    if (strategicImperative) {
      const fieldsName = Object.keys(strategicImperativeFormConfig);
      setFieldShowError(true, fieldsName);
    }
  }, [setFieldShowError, strategicImperative, strategicImperativeFormConfig]);

  const handleCancelStrategicImperativeForm = () => {
    resetForm();
    cancelCallback();
  };

  const handleSaveStrategicImperativeForm = async () => {
    setIsSubmitting(true);
    const formValues = getValues();
    const formattedSI = strategicImperativeToBE(formValues);

    await submitCallback(formattedSI);

    setIsSubmitting(false);
  };

  const cancelButtonConfig = {
    buttonProps: {
      onClick: handleCancelStrategicImperativeForm,
      buttonType: ButtonType.secondary,
    },
    label: t('actions_cancel_label'),
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: handleSaveStrategicImperativeForm,
      buttonType: ButtonType.primary,
      disabled: !isValid || isSubmitting,
    },
    label: t('actions_save_label'),
    loading: isSubmitting,
  };

  return {
    cancelButtonConfig,
    strategicImperativeFormConfig,
    submitButtonConfig,
    providerFields,
    t,
  };
};

export default useStrategicImperativeCreate;
